import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Model, CreateModelForm } from '../../types';
import { useProjectStore } from '../../store';
import ModelRegistryTab from './ModelRegistryTab';
import EntityHistoryTab from '../history/EntityHistoryTab';
import HistoryDropdown from '../history/HistoryDropdown';
import CustomSelect from '../common/CustomSelect';
import {
  normalizeProperty,
  serializeProperty,
  isNumericType,
} from '../../utils/propertyMeta';

// Available Pydantic types (extend as needed)
const BASE_TYPES = [
  { label: 'String', value: 'str' },
  { label: 'Integer', value: 'int' },
  { label: 'Float', value: 'float' },
  { label: 'Boolean', value: 'bool' },
  { label: 'Datetime', value: 'datetime' },
  { label: 'Literal', value: 'Literal' },
  { label: 'List', value: 'list' },
];

function getAllTypeOptions(existingModels: Model[], currentClassName: string) {
  const customTypes = existingModels
    .filter(m => m.class_name !== currentClassName)
    .map(m => ({ label: m.class_name, value: m.class_name }));
  return [
    { label: 'Base Types', options: BASE_TYPES },
    { label: 'Custom Types', options: customTypes }
  ];
}

interface Property {
  name: string;
  type: string;
  literalValues?: string[];
  listElementType?: string; // For list properties
  // AC-1253 rich metadata fields (all optional)
  description?: string;
  min?: number;
  max?: number;
}

interface ModelFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (model: CreateModelForm, editId?: number) => void;
  existingModels: Model[];
  editModel?: Model | null;
  initialTab?: 'properties' | 'registry';
  initialRegistrySubTab?: 'configuration' | 'entries';
  onRegistryChange?: () => void;
  /** Pre-fill values for the create form (e.g. when generating from a tool output example) */
  initialClassName?: string;
  initialDescription?: string;
  initialProperties?: Property[];
}

const ModelFormModal: React.FC<ModelFormModalProps> = ({
  open,
  onClose,
  onSave,
  existingModels,
  editModel,
  initialTab,
  initialRegistrySubTab,
  onRegistryChange,
  initialClassName,
  initialDescription,
  initialProperties,
}) => {
  const { currentProject } = useProjectStore();
  const [className, setClassName] = useState('');
  const [description, setDescription] = useState('');
  const [properties, setProperties] = useState<Property[]>([]);
  const [literalInputs, setLiteralInputs] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'properties' | 'registry' | 'history'>('properties');
  const [focusNewProperty, setFocusNewProperty] = useState(false);
  const lastPropertyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      if (editModel) {
        setClassName(editModel.class_name);
        setDescription(editModel.description || '');
        setProperties(
          Object.entries(editModel.properties).map(([name, rawValue]) => {
            // Rich value may be either a string shorthand or an object.
            const meta = normalizeProperty(rawValue as any);
            const type = meta.type;
            let propType: string = type;
            let literalValues: string[] | undefined = undefined;
            let listElementType: string | undefined = undefined;

            if (type.startsWith('Literal[')) {
              propType = 'Literal';
              literalValues = type.slice(8, -1).split(',').map(v => v.trim().replace(/['"]/g, ''));
            } else if (type.startsWith('list[') && type.endsWith(']')) {
              propType = 'list';
              listElementType = type.slice(5, -1).trim();
            }

            return {
              name,
              type: propType,
              literalValues,
              listElementType,
              description: meta.description,
              min: meta.min,
              max: meta.max,
            };
          })
        );
      } else {
        setClassName(initialClassName || '');
        setDescription(initialDescription || '');
        setProperties(initialProperties ? [...initialProperties] : []);
        setLiteralInputs({});
      }
      setErrors([]);
      // Set initial tab if provided (e.g., from RegistriesPage), otherwise reset to properties
      setActiveTab(initialTab || 'properties');
    }
  }, [open, editModel, initialTab, initialClassName, initialDescription, initialProperties]);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    // Validate class name
    if (!className) {
      newErrors.push('Schema name is required');
    } else if (!/^[A-Z][a-zA-Z0-9]*$/.test(className)) {
      newErrors.push('Schema name must start with a capital letter and contain only letters and numbers');
    } else if (existingModels.some(m => m.class_name === className && m.model_id !== editModel?.model_id)) {
      newErrors.push('A schema with this name already exists');
    }

    // Validate properties
    properties.forEach((prop, idx) => {
      if (!prop.name) {
        newErrors.push(`Property ${idx + 1} name is required`);
      } else if (!/^[a-z][a-zA-Z0-9_]*$/.test(prop.name)) {
        newErrors.push(`Property ${idx + 1} name must start with a lowercase letter and contain only letters, numbers, and underscores`);
      }
      if (!prop.type) {
        newErrors.push(`Property ${idx + 1} type is required`);
      }
      if (prop.type === 'Literal' && (!prop.literalValues || prop.literalValues.length === 0)) {
        newErrors.push(`Property ${idx + 1} requires at least one literal value`);
      }
      if (prop.type === 'list' && !prop.listElementType) {
        newErrors.push(`Property ${idx + 1} requires an element type for the list`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handlePropertyChange = (idx: number, field: keyof Property, value: string) => {
    setProperties(prev => {
      const newProps = [...prev];
      if (field === 'type') {
        if (value !== 'Literal') {
          delete newProps[idx].literalValues;
        }
        if (value !== 'list') {
          delete newProps[idx].listElementType;
        }
        // Clear Min/Max when switching to a non-numeric type
        if (!isNumericType(value)) {
          delete newProps[idx].min;
          delete newProps[idx].max;
        }
      }
      if (field === 'min' || field === 'max') {
        // Parse numeric input; empty string clears the field
        const parsed = value === '' ? undefined : Number(value);
        newProps[idx] = {
          ...newProps[idx],
          [field]: Number.isFinite(parsed) ? (parsed as number) : undefined,
        };
        return newProps;
      }
      newProps[idx] = { ...newProps[idx], [field]: value };
      return newProps;
    });
  };

  const handleAddProperty = () => {
    setProperties(prev => [...prev, { name: '', type: '' }]);
    setFocusNewProperty(true);
  };

  useEffect(() => {
    if (focusNewProperty && lastPropertyRef.current) {
      lastPropertyRef.current.focus();
      setFocusNewProperty(false);
    }
  }, [focusNewProperty, properties.length]);

  const handleRemoveProperty = (idx: number) => {
    setProperties(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddLiteralValue = (idx: number) => {
    const value = (literalInputs[idx] || '').trim();
    if (!value) return;
    setProperties(prev => {
      const newProps = [...prev];
      const values = newProps[idx].literalValues || [];
      if (!values.includes(value)) {
        newProps[idx].literalValues = [...values, value];
      }
      return newProps;
    });
    setLiteralInputs(prev => ({ ...prev, [idx]: '' }));
  };

  const handleRemoveLiteralValue = (idx: number, value: string) => {
    setProperties(prev => {
      const newProps = [...prev];
      newProps[idx].literalValues = (newProps[idx].literalValues || []).filter(v => v !== value);
      return newProps;
    });
  };

  const handleLiteralInputChange = (idx: number, value: string) => {
    setLiteralInputs(prev => ({ ...prev, [idx]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const modelData: CreateModelForm = {
        class_name: className,
        description,
        properties: Object.fromEntries(
          properties.map(prop => {
            // Build the canonical type string (same rules as before).
            const typeStr =
              prop.type === 'Literal' && prop.literalValues
                ? `Literal[${prop.literalValues.map(v => `'${v}'`).join(', ')}]`
                : prop.type === 'list' && prop.listElementType
                ? `list[${prop.listElementType}]`
                : prop.type;
            // AC-1253: emit shorthand when no metadata, rich object otherwise.
            const serialized = serializeProperty({
              type: typeStr,
              description: prop.description,
              min: prop.min,
              max: prop.max,
            });
            return [prop.name, serialized];
          })
        ) as any,
      };

      await onSave(modelData, editModel?.model_id);
    } catch (err) {
      setErrors(['Failed to save schema. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = () => {
    // AC-1251 M4: when a property has rich metadata (description/min/max),
    // render it as ``<name>: <type> = Field(description=..., ge=..., le=...)``
    // so the preview reflects the Pydantic shape the backend actually emits.
    // When no metadata is set, keep the bare ``<name>: <type>`` form.
    const formatFieldKwargs = (prop: Property): string => {
      const parts: string[] = [];
      if (prop.description && prop.description.trim() !== '') {
        // Escape any embedded double quotes so the preview stays valid Python.
        const escaped = prop.description.replace(/"/g, '\\"');
        parts.push(`description="${escaped}"`);
      }
      if (isNumericType(prop.type)) {
        if (prop.min !== undefined && Number.isFinite(prop.min)) {
          parts.push(`ge=${prop.min}`);
        }
        if (prop.max !== undefined && Number.isFinite(prop.max)) {
          parts.push(`le=${prop.max}`);
        }
      }
      return parts.length > 0 ? ` = Field(${parts.join(', ')})` : '';
    };

    const props = properties.map(prop => {
      let typeStr: string;
      if (prop.type === 'Literal' && prop.literalValues) {
        typeStr = `Literal[${prop.literalValues.map(v => `'${v}'`).join(', ')}]`;
      } else if (prop.type === 'list' && prop.listElementType) {
        typeStr = `list[${prop.listElementType}]`;
      } else {
        typeStr = prop.type;
      }
      return `    ${prop.name}: ${typeStr}${formatFieldKwargs(prop)}`;
    }).join('\n');

    return `class ${className}:\n${props}`;
  };

  return open ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
              {editModel ? 'Edit Schema' : 'Create New Schema'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Project: {currentProject?.name || 'Unknown Project'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* History dropdown - only for existing models */}
            {editModel?.model_id && currentProject && (
              <HistoryDropdown
                entityType="model"
                entityId={editModel.model_id}
                projectId={parseInt(String(currentProject.project_id), 10)}
                onHistoryClick={() => setActiveTab('history')}
              />
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs - Only show when editing an existing model */}
        {editModel && (
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('properties')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'properties'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                Properties
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('registry')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'registry'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                Registry
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                History
              </button>
            </nav>
          </div>
        )}

        {/* Registry Tab Content */}
        {editModel && activeTab === 'registry' && (
          <div className="flex-1 overflow-y-auto">
            <ModelRegistryTab model={editModel} initialSubTab={initialRegistrySubTab} onRegistryChange={onRegistryChange} />
          </div>
        )}

        {/* History Tab Content */}
        {editModel && activeTab === 'history' && currentProject && (
          <div className="flex-1 overflow-y-auto p-6">
            <EntityHistoryTab
              entityType="model"
              entityId={editModel.model_id}
              projectId={parseInt(String(currentProject.project_id), 10)}
            />
          </div>
        )}

        {/* Properties Form - shown when Properties tab is active or when creating new model */}
        {(activeTab === 'properties' || !editModel) && (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" autoComplete="off">
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Schema Name
              </label>
              <input
                type="text"
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., UserProfile"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Must start with a capital letter and contain only letters and numbers
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Describe the purpose of this schema..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">Properties</h3>
                <button
                  type="button"
                  onClick={handleAddProperty}
                  className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Property
                </button>
              </div>

              <div className="space-y-4">
                {properties.map((prop, idx) => (
                  <div key={idx} className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 flex items-center gap-4">
                        <div className="w-1/3">
                          <input
                            ref={idx === properties.length - 1 ? lastPropertyRef : undefined}
                            type="text"
                            value={prop.name}
                            onChange={(e) => handlePropertyChange(idx, 'name', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (prop.name.trim()) {
                                  handleAddProperty();
                                }
                              }
                            }}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Property name"
                          />
                        </div>
                        <div className="w-1/3">
                          <CustomSelect
                            value={prop.type}
                            onChange={(value) => handlePropertyChange(idx, 'type', value)}
                            options={getAllTypeOptions(existingModels, className).flatMap((group) =>
                              group.options.map((type) => ({ value: type.value, label: `${type.label} (${group.label})` }))
                            )}
                            placeholder="Select type"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        {prop.type === 'Literal' && (
                          <div className="w-1/3 flex items-center gap-2">
                            <input
                              type="text"
                              value={literalInputs[idx] || ''}
                              onChange={(e) => handleLiteralInputChange(idx, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddLiteralValue(idx);
                                }
                              }}
                              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              placeholder="Add value..."
                            />
                            <button
                              type="button"
                              onClick={() => handleAddLiteralValue(idx)}
                              className="px-2 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                            >
                              Add
                            </button>
                          </div>
                        )}
                        {prop.type === 'list' && (
                          <div className="w-1/3">
                            <CustomSelect
                              value={prop.listElementType || ''}
                              onChange={(value) => handlePropertyChange(idx, 'listElementType', value)}
                              options={getAllTypeOptions(existingModels, className).flatMap((group) =>
                                group.options.map((type) => ({ value: type.value, label: `${type.label} (${group.label})` }))
                              )}
                              placeholder="Select element type"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveProperty(idx)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* AC-1253: Rich property metadata - description always visible,
                        min/max only for numeric types. */}
                    <div className="flex flex-wrap items-center gap-2 pl-0">
                      <input
                        type="text"
                        value={prop.description ?? ''}
                        onChange={(e) => handlePropertyChange(idx, 'description', e.target.value)}
                        className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g. Composite quality score (description)"
                        aria-label={`Description for property ${prop.name || idx + 1}`}
                      />
                      {isNumericType(prop.type) && (
                        <>
                          <input
                            type="number"
                            value={prop.min ?? ''}
                            onChange={(e) => handlePropertyChange(idx, 'min', e.target.value)}
                            className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Min"
                            aria-label={`Min value for property ${prop.name || idx + 1}`}
                          />
                          <input
                            type="number"
                            value={prop.max ?? ''}
                            onChange={(e) => handlePropertyChange(idx, 'max', e.target.value)}
                            className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Max"
                            aria-label={`Max value for property ${prop.name || idx + 1}`}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add Property button below last property */}
                <button
                  type="button"
                  onClick={handleAddProperty}
                  className="flex items-center gap-1 w-full px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Property
                </button>

                {properties.some(prop => prop.type === 'Literal' && prop.literalValues?.length) && (
                  <div className="mt-2 space-y-2">
                    {properties.map((prop, idx) => (
                      prop.type === 'Literal' && prop.literalValues?.length ? (
                        <div key={idx} className="flex flex-wrap gap-2">
                          {prop.literalValues.map((value) => (
                            <div
                              key={value}
                              className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                            >
                              <span className="text-gray-900 dark:text-warm-white">{value}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveLiteralValue(idx, value)}
                                className="text-gray-400 hover:text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </div>

            {errors.length > 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                {errors.map((error, idx) => (
                  <p key={idx} className="text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {error}
                  </p>
                ))}
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white mb-2">Schema Preview</h3>
              <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-sm font-mono">
                {String(generatePreview())}
              </pre>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : editModel ? 'Update Schema' : 'Create Schema'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  ) : null;
};

export default ModelFormModal; 